import { Injectable, inject } from '@angular/core';
import { CanActivate, CanActivateFn, Router, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { content } from 'src/environments/data';
import { webddn } from 'src/environments/webddn';
import { PatientIntakeGateService } from './services/patient-intake-gate.service';
import { UserService } from './services/user.service';

@Injectable({ providedIn: 'root' })
export class PatientIntakeRequiredGuard implements CanActivate {
  constructor(
    private userService: UserService,
    private gate: PatientIntakeGateService,
    private router: Router
  ) {}

  canActivate(): Observable<boolean | UrlTree> {
    const user = this.userService.getCurrentUser();
    if (!user?.token) {
      return of(true);
    }
    if (webddn.APP_TYPE !== 'health' && webddn.APP_TYPE !== 'optical' || user.role !== 'u') {
      return of(true);
    }
    const path = this.normalizePath(this.router.url);
    if (this.isExempt(path)) {
      return of(true);
    }
    return this.gate.patientIntakePending$(user.role).pipe(
      take(1),
      map((pending) => (pending ? this.router.createUrlTree(['/patient-intake']) : true))
    );
  }

  private normalizePath(url: string): string {
    const noQuery = url.split('?')[0];
    const noHash = noQuery.split('#')[0];
    return noHash || '/';
  }

  private isExempt(path: string): boolean {
    if (path === '/patient-intake' || path.startsWith('/patient-intake/')) {
      return true;
    }
    const signupBase = '/' + content.WEBDDN_SIGNUP_PATH;
    if (path === signupBase || path.startsWith(signupBase + '/')) {
      return true;
    }
    const loginPath = '/' + content.WEBDDN_LOGIN_PATH;
    if (path === loginPath || path.startsWith(loginPath + '/')) {
      return true;
    }
    if (path.startsWith('/forgot-password') || path.startsWith('/reset-password')) {
      return true;
    }
    if (path.startsWith('/signout')) {
      return true;
    }
    if (path.startsWith('/customer-approval')) {
      return true;
    }
    return false;
  }
}

/**
 * Modern Functional Route Guard for Angular 22
 */
export const patientIntakeRequiredGuard: CanActivateFn = () => {
  return inject(PatientIntakeRequiredGuard).canActivate();
};
